import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Clock, CheckCircle, XCircle, Trash2, CalendarRange, User, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  approved: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  rejected: { color: 'bg-rose-100 text-rose-700 border-rose-200', icon: XCircle },
};

export default function LongPeriodRequestsPage() {
  const { t, dateLocale } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/long-period-requests`, { withCredentials: true });
      setRequests(data);
    } catch {
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleUpdate = async (id, status, adminNotes) => {
    setUpdatingId(id);
    try {
      await axios.put(`${API_URL}/api/admin/long-period-requests/${id}`, { status, admin_notes: adminNotes }, { withCredentials: true });
      toast.success(`${t('requestStatus')}: ${t(status)}`);
      await fetchRequests();
    } catch {
      toast.error(t('error'));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/long-period-requests/${id}`, { withCredentials: true });
      toast.success(t('delete'));
      await fetchRequests();
    } catch {
      toast.error(t('error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const otherRequests = requests.filter(r => r.status !== 'pending');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="long-period-requests-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <CalendarRange className="w-6 h-6" />
            {t('longPeriodRequests')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">{t('longPeriodDesc')}</p>
        </div>

        {requests.length === 0 ? (
          <Card className="card-floating">
            <CardContent className="py-12 text-center">
              <CalendarRange className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">{t('noRequests')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Pending */}
            {pendingRequests.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-amber-700 mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5" /> {t('pending')} ({pendingRequests.length})
                </h2>
                <div className="space-y-4">
                  {pendingRequests.map(req => (
                    <RequestCard key={req.id} req={req} t={t} dateLocale={dateLocale}
                      onUpdate={handleUpdate} onDelete={handleDelete} updatingId={updatingId} />
                  ))}
                </div>
              </div>
            )}

            {/* Processed */}
            {otherRequests.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-slate-600 mb-3">{t('allRequests')}</h2>
                <div className="space-y-3">
                  {otherRequests.map(req => (
                    <RequestCard key={req.id} req={req} t={t} dateLocale={dateLocale}
                      onUpdate={handleUpdate} onDelete={handleDelete} updatingId={updatingId} compact />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function RequestCard({ req, t, dateLocale, onUpdate, onDelete, updatingId, compact }) {
  const [adminNotes, setAdminNotes] = useState(req.admin_notes || '');
  const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;
  const days = differenceInDays(parseISO(req.end_date), parseISO(req.start_date)) + 1;
  const isUpdating = updatingId === req.id;

  return (
    <Card className={`card-floating border-l-4 ${req.status === 'pending' ? 'border-l-amber-400' : req.status === 'approved' ? 'border-l-emerald-400' : 'border-l-rose-400'}`}
      data-testid={`lp-request-${req.id}`}>
      <CardContent className={compact ? 'p-4' : 'p-5'}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 space-y-2">
            {/* User info */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border ${statusCfg.color}`}>
                <StatusIcon className="w-3 h-3" /> {t(req.status)}
              </span>
              <span className="text-sm font-semibold text-slate-800 flex items-center gap-1">
                <User className="w-3 h-3" /> {req.user_name}
              </span>
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Mail className="w-3 h-3" /> {req.user_email}
              </span>
            </div>

            {/* Dates */}
            <div className="flex items-center gap-2 text-sm">
              <CalendarRange className="w-4 h-4 text-blue-500" />
              <span className="font-semibold text-slate-700">
                {format(parseISO(req.start_date), 'dd MMM yyyy', { locale: dateLocale })}
                {' '}&rarr;{' '}
                {format(parseISO(req.end_date), 'dd MMM yyyy', { locale: dateLocale })}
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                {days} {days === 1 ? t('day') : t('days')}
              </span>
              {req.desk_id && (
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                  {t('desk')} {req.desk_id}
                </span>
              )}
            </div>

            {/* User notes */}
            {req.notes && (
              <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2">{req.notes}</p>
            )}
          </div>

          {/* Admin actions */}
          <div className="flex flex-col gap-2 min-w-[200px]">
            {req.status === 'pending' && (
              <>
                <Textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  placeholder={t('adminNotes')}
                  className="rounded-xl text-xs h-16 resize-none"
                  data-testid={`lp-admin-notes-${req.id}`}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => onUpdate(req.id, 'approved', adminNotes)}
                    disabled={isUpdating} data-testid={`lp-approve-${req.id}`}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs h-8">
                    {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" />{t('approve')}</>}
                  </Button>
                  <Button size="sm" onClick={() => onUpdate(req.id, 'rejected', adminNotes)}
                    disabled={isUpdating} data-testid={`lp-reject-${req.id}`}
                    className="flex-1 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs h-8">
                    {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : <><XCircle className="w-3 h-3 mr-1" />{t('reject')}</>}
                  </Button>
                </div>
              </>
            )}
            {req.status !== 'pending' && req.admin_notes && (
              <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
                <span className="font-bold">{t('adminNotes')}:</span> {req.admin_notes}
              </p>
            )}
            <Button size="sm" variant="ghost" onClick={() => onDelete(req.id)}
              data-testid={`lp-delete-${req.id}`}
              className="text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-full text-xs h-7">
              <Trash2 className="w-3 h-3 mr-1" /> {t('delete')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
